import React, { useState, useEffect } from "react";
import CardLayout from "../../../../common/cardLayout/CardLayout";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { generateYAxisTicks } from "../../../../../util/helpers";
import PatientLoader from "../../../../common/PatientLoader";
import DateRangeComponent from "../DateRangeComponent";
import { NoAssessments } from "../FlaggedJournals";
import AssessmentsOverview from "./AssessmentsOverview";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const CheckInOverviewTable = ({
  setCheckinTab = null,
  size = "7",
  showHeading = true,
}) => {
  const { assessmentsLoading, assessmentsData } = useSelector(
    (state) => state.assessment
  );

  const { singlePatientData } = useSelector((state) => state.patient);

  function normailzeData(data) {
    const formattedData = {};
    data?.forEach((item) => {
      const date = moment(item?.created).format("MMM DD");
      if (date in formattedData) {
        formattedData[date] += item?.score;
      } else {
        formattedData[date] = item?.score;
      }
    });
    return formattedData;
  }

  function generateXAxisData(arr1, arr2, arr3,arr4) {
    arr1 = Object.keys(arr1);
    arr2 = Object.keys(arr2);
    arr3 = Object.keys(arr3);
    arr4 = Object.keys(arr4);
    const res = [...new Set([...arr1, ...arr2, ...arr3, ...arr4])];
    return res;
  }

  const [labels, setLabels] = useState([]);
  const [phq9, setPhq9] = useState([]);
  const [PhqA, setPhqA] = useState([]);
  const [phq8, setPhq8] = useState([]);
  const [gad7, setGad7] = useState([]);
  const [transparentAxis, setTransparentAxis] = useState([]);
  const [noData, setNoData] = useState(false);
  const [globalMinima, setGlobalMinima] = useState(0);
  const [globalMaxima, setGlobalMaxima] = useState(40);

  // const [yAxisLookup, setYAxisLookup] = useState({
  //   0: "",
  //   8: "😄 Great",
  //   16: "😁 Good",
  //   24: "🙂 Okay",
  //   32: "😢 Bad",
  //   40: "😭 Awful",
  // });
  // const [yAxisLookup, setYAxisLookup] = useState({
  //   0: "😄 Great",
  //   4: "😁 Good",
  //   8: "🙂 Okay",
  //   12: "😢 Bad",
  //   17:"😭 Awful",
  // });
  //
  const [yAxisLookup, setYAxisLookup] = useState({
    0: "😄 Great",
    5: "😁 Good",
    10: "🙂 Okay",
    15: "😢 Bad",
    20:"😭 Awful",
  });
  const paddingData = (xAxis, dataArray) => {
    //*Padding data to have null values where dates don't match up
    const temp = xAxis.map((item) => {
      if (item in dataArray) {
        return dataArray[item];
      }
      return null;
    });
    return temp;
  };

  const phqType =
    process.env.REACT_APP_DASHBOARD_TYPE === "youth" ? "PHQ-A" : "";
  useEffect(() => {
    if (!assessmentsLoading) {
      if (assessmentsData && "PHQ-8" in assessmentsData) {
        const condition1 =
          "PHQ-8" in assessmentsData && assessmentsData["PHQ-8"]?.length !== 0;
        const condition2 =
        phqType in assessmentsData && assessmentsData[phqType]?.length !== 0;
        const condition3 =
          "GAD-7" in assessmentsData && assessmentsData["GAD-7"]?.length !== 0;
        const condition4 =
           "PHQ-9" in assessmentsData && assessmentsData["PHQ-9"]?.length !== 0;
        if (!condition1 && !condition2 && !condition3 && !condition4) {
          setNoData(true);
          return;
        }
        setNoData(false);
        let data1 = normailzeData(assessmentsData["PHQ-8"]);
        let data2 = normailzeData(assessmentsData[ phqType]);
        let data3 = normailzeData(assessmentsData["GAD-7"]);
        let data4 = normailzeData(assessmentsData["PHQ-9"]);
        const xAxis = generateXAxisData(data1, data2, data3,data4);

        //Padding data to have null values where dates don't match up
        data1 = paddingData(xAxis, data1);
        setPhq8(data1);
        data2 = paddingData(xAxis, data2);
        setPhqA(data2);
        data3 = paddingData(xAxis, data3);
        setGad7(data3);
        data4 = paddingData(xAxis, data4);
        setPhq9(data4);

        const maxima = Math.max(...[...data1, ...data2, ...data3, ...data4]) + 10;
        setGlobalMaxima(maxima);
        setTransparentAxis([0, maxima]);
        setLabels([...xAxis]);
      }
    }
  }, [assessmentsLoading, assessmentsData]);
  let chartData = {}
  if (process.env.REACT_APP_DASHBOARD_TYPE === "youth"){
    chartData = {
      labels,
      datasets: [
        {
          label:  phqType,
          data: PhqA,
          borderColor: "#FFB200",
          backgroundColor: "#FFB200",
        },
        {
          label: "PHQ-9",
          data: phq9,
          borderColor: "#FF3A29",
          backgroundColor: "#FF3A29",
        },
        //! REMOVED BECAUSE PHQ-8 IS NOT CONSIDERED
        // {
        //   label: "PHQ 8",
        //   data: phq8,
        //   borderColor: "#FF3A29",
        //   backgroundColor: "#FF3A29",
        // },
        {
          label: "GAD 7",
          data: gad7,
          borderColor: "#4339F2",
          backgroundColor: "#4339F2",
        },
        {
          label: "",
          data: transparentAxis,
          borderColor: "transparent",
          backgroundColor: "transparent",
        },
      ],
    };
  }else {
    chartData = {
      labels,
      datasets: [
        {
          label: "PHQ-9",
          data: gad7,
          borderColor: "#FF3A29",
          backgroundColor: "#FF3A29",
        },
        //! REMOVED BECAUSE PHQ-8 IS NOT CONSIDERED
        // {
        //   label: "PHQ 8",
        //   data: phq8,
        //   borderColor: "#FF3A29",
        //   backgroundColor: "#FF3A29",
        // },
        {
          label: "GAD 7",
          data: gad7,
          borderColor: "#4339F2",
          backgroundColor: "#4339F2",
        },
        {
          label: "",
          data: transparentAxis,
          borderColor: "transparent",
          backgroundColor: "transparent",
        },
      ],
    };
  }
 

  const options = {
    responsive: true,
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        min: 0,
        max: 30,
        ticks: {
          stepSize:5,
          // maxTicksLimit: 15,
          callback: (value) => {
            return yAxisLookup[value];
          },
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          usePointStyle: true,
          boxWidth: 12,
          boxHeight: 10,
          useBorderRadius: true,
          borderRadius: 100,
          pointStyle: "circle",
          family: "'DM Sans'",
        },
        position: "top",
        align: "start",
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <CardLayout
      className={`col-sm-12 col-md-${size} col-lg-${size}`}
      style={{
        border: "8px solid transparent",
        boxSizing: "border-box",
        backgroundClip: "padding-box",
      }}
    >
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between mb-4 mt-2">
          {showHeading && (
            <label style={{ fontSize: "1.3rem" }}>Assessment Overview</label>
          )}

          {setCheckinTab !== null ? (
            <small
              onClick={() => {
                setCheckinTab();
              }}
              style={{
                color: "#395e9d",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              View more {">"}
            </small>
          ) : (
            <></>
          )}
        </div>
        {noData ? (
          <NoAssessments
            text={`${
              singlePatientData?.first_name || "Patient"
            } has not taken this assessment!`}
            show_link={false}
          />
        ) : (
          <>
            {assessmentsLoading ? (
              <PatientLoader showText={false} />
            ) : (
              <div className="container-fluid">
                <Line options={options} data={chartData} />
              </div>
            )}
          </>
        )}
      </div>
    </CardLayout>
  );
};
const CheckInOverview = (props) => {
  const { singlePatientData } = useSelector((state) => state.patient);
  const { startDate, setStartDate, endDate, setEndDate } = props;
  const [checkins, setCheckins] = useState(null);
  return (
    <div className="container-fluid m-0 mb-5 p-0 pt-2">
      <div className="d-flex" style={{ gap: "10px" }}>
        <DateRangeComponent
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </div>
      <div className="row">
        <AssessmentsOverview size="8" setCheckins={setCheckins} />
        <div className="col-sm-12 col-md-4 col-lg-4">
          <CardLayout
            className="d-flex flex-column mb-2 py-3 px-5"
            style={{ alignItems: "start", height: "48%" }}
          >
            <span className="mt-4">
              <span className="muted-text">Last Check In</span>
              <h2 style={{ color: "#333333", fontSize: "2.3vw" }}>
                {singlePatientData?.last_active
                  ? moment(singlePatientData?.last_active).format(
                      "MMM Do, YYYY"
                    )
                  : "Not Available"}
              </h2>
            </span>
          </CardLayout>
          <CardLayout
            className="d-flex flex-column mb-2 py-3 px-5"
            style={{ alignItems: "start", height: "48%" }}
          >
            <span className="mt-4">
              <span className="muted-text">Check Ins this period</span>
              <h2 style={{ color: "#333333", fontSize: "2.3vw" }}>
                {checkins || "N/A"}
              </h2>
            </span>
          </CardLayout>
          {/* <CardLayout
            className="d-flex flex-column mb-2 py-3 px-5"
            style={{ alignItems: "start", height: "32%" }}
          >
            <span className="mt-4">
              <span className="muted-text">Flagged Check In</span>
              <h2 style={{ color: "#333333", fontSize: "2.3vw" }}>N/A</h2>
            </span>
          </CardLayout> */}
        </div>
      </div>
    </div>
  );
};

export default CheckInOverview;
